/**
 * Life4Earth V2 — Client-Side Image Compression
 *
 * Resizes and compresses images in the browser before upload.
 * Uses the Canvas API — no external dependencies.
 *
 * Pipeline:
 *   1. Load image into an <img> via FileReader
 *   2. Draw onto a canvas scaled to maxDimension
 *   3. Export as PNG (preserves alpha) or JPEG depending on input type
 *   4. If over maxBytes, retry at lower quality / smaller dimension
 *   5. Return the Blob + dimensions
 */

export interface CompressedImage {
  blob: Blob;
  width: number;
  height: number;
}

export interface CompressOptions {
  /** Maximum pixel dimension (width or height). Default 1600. */
  maxDimension?: number;
  /** JPEG quality (0–1). Default 0.82. Ignored for PNG output. */
  quality?: number;
  /** Target max file size in bytes. Default 500_000 (500 KB). */
  maxBytes?: number;
}

/** MIME types that support alpha channels — export as PNG to preserve transparency. */
const ALPHA_TYPES = new Set(['image/png', 'image/webp', 'image/svg+xml']);

/**
 * Compress an image file within the target size.
 *
 * - PNG/WebP/SVG inputs → exported as PNG to preserve alpha transparency
 * - All other inputs → exported as JPEG at target quality
 *
 * Three-tier strategy:
 *   Pass 1: maxDimension @ quality (default 1600px / 0.82)
 *   Pass 2: maxDimension @ 0.70 (if still over maxBytes)
 *   Pass 3: 1200px @ 0.70 (if still over maxBytes)
 */
export async function compressImage(
  file: File,
  options: CompressOptions = {}
): Promise<CompressedImage> {
  const {
    maxDimension = 1600,
    quality = 0.82,
    maxBytes = 500_000,
  } = options;

  const preserveAlpha = ALPHA_TYPES.has(file.type);

  // Load the image
  const img = await loadImage(file);

  // Pass 1: full dimension, target quality
  let result = resizeAndExport(img, maxDimension, quality, preserveAlpha);
  if (result.blob.size <= maxBytes) return result;

  // Pass 2: same dimension, lower quality
  result = resizeAndExport(img, maxDimension, 0.70, preserveAlpha);
  if (result.blob.size <= maxBytes) return result;

  // Pass 3: reduced dimension, lower quality
  result = resizeAndExport(img, 1200, 0.70, preserveAlpha);
  return result; // Accept whatever size this produces
}

/* ------------------------------------------------------------------ */
/*  Internal helpers                                                   */
/* ------------------------------------------------------------------ */

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function resizeAndExport(
  img: HTMLImageElement,
  maxDim: number,
  quality: number,
  preserveAlpha: boolean
): CompressedImage {
  let { naturalWidth: w, naturalHeight: h } = img;

  // Scale down if larger than maxDim
  if (w > maxDim || h > maxDim) {
    const scale = maxDim / Math.max(w, h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  if (preserveAlpha) {
    // Clear canvas to fully transparent before drawing
    ctx.clearRect(0, 0, w, h);
  }

  ctx.drawImage(img, 0, 0, w, h);

  // PNG for alpha-capable sources, JPEG for everything else
  const mime = preserveAlpha ? 'image/png' : 'image/jpeg';
  const dataUrl = preserveAlpha
    ? canvas.toDataURL('image/png')
    : canvas.toDataURL('image/jpeg', quality);
  const blob = dataUrlToBlob(dataUrl);

  return { blob, width: w, height: h };
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}
